import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallbackTitle?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    this.setState({ componentStack: info.componentStack || null });
  }

  render() {
    if (this.state.hasError) {
      const title = this.props.fallbackTitle || 'Something went wrong';
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '64px 32px',
          textAlign: 'center',
          minHeight: 300,
        }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: 'rgba(239,68,68,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 16,
            fontSize: 22,
          }}>
            !
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 8px' }}>
            {title}
          </h2>
          <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '0 0 4px', maxWidth: 420 }}>
            A service may be unreachable. Try refreshing the page.
          </p>
          {this.state.error && (
            <code style={{
              fontSize: 11,
              color: '#ef4444',
              background: 'rgba(239,68,68,0.06)',
              padding: '4px 10px',
              borderRadius: 6,
              marginTop: 8,
              maxWidth: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {this.state.error.message}
            </code>
          )}
          {this.state.componentStack && (
            <details style={{ marginTop: 12, maxWidth: 500, textAlign: 'left' }}>
              <summary style={{ fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer' }}>Component stack</summary>
              <pre style={{
                fontSize: 10,
                color: 'var(--text-secondary)',
                background: 'rgba(0,0,0,0.03)',
                padding: '8px 10px',
                borderRadius: 6,
                marginTop: 4,
                maxHeight: 200,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                {this.state.componentStack}
              </pre>
            </details>
          )}
          <button
            onClick={() => this.setState({ hasError: false, error: null, componentStack: null })}
            style={{
              marginTop: 20,
              padding: '8px 20px',
              background: 'rgba(13,148,136,0.1)',
              color: '#0D9488',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
